/* ==========================================================================
   NovaCoir — interaction layer.

   Everything here is progressive enhancement. With JS off:
     - showcase cards all stay readable (CSS falls back to an even grid)
     - the accordion still works, because it is <details>/<summary>
     - the testimonial track is still swipeable and keyboard-scrollable
     - the video section shows its poster as a still
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Showcase: one expanded card at a time ----------------------------- */
  function initShowcase(track) {
    var cards = Array.prototype.slice.call(track.querySelectorAll('[data-nova-card]'));
    if (cards.length < 2) return;

    track.classList.add('is-enhanced');

    function activate(index) {
      var next = (index + cards.length) % cards.length;
      cards.forEach(function (card, i) {
        var on = i === next;
        card.classList.toggle('is-active', on);
        var toggle = card.querySelector('[data-nova-card-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      track.dataset.novaActive = String(next);
    }

    cards.forEach(function (card, i) {
      var toggle = card.querySelector('[data-nova-card-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        activate(i);
      });
      // Pointer users get it on hover too, but never on touch (where hover
      // fires as a phantom event before the click).
      card.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'mouse') activate(i);
      });
    });

    var section = track.closest('.nova-showcase');
    var nav = section && section.querySelector('[data-nova-showcase-nav]');
    if (nav) {
      var prev = nav.querySelector('[data-nova-prev]');
      var next = nav.querySelector('[data-nova-next]');
      if (prev) {
        prev.addEventListener('click', function () {
          activate(parseInt(track.dataset.novaActive || '0', 10) - 1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          activate(parseInt(track.dataset.novaActive || '0', 10) + 1);
        });
      }
    }

    activate(0);
  }

  /* --- Accordion: opening one row closes the others ---------------------- */
  function initAccordion(list) {
    var items = Array.prototype.slice.call(list.querySelectorAll('details'));
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* --- Horizontal scrollers --------------------------------------------- */
  function initScroller(section) {
    var track = section.querySelector('[data-nova-scroll-track]');
    if (!track) return;
    var prev = section.querySelector('[data-nova-scroll-prev]');
    var next = section.querySelector('[data-nova-scroll-next]');

    function step(direction) {
      var first = track.firstElementChild;
      var amount = first ? first.getBoundingClientRect().width + 32 : track.clientWidth * 0.8;
      track.scrollBy({
        left: amount * direction,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* --- Video: swap the poster for the real player on demand -------------- */
  function initVideo(frame) {
    var button = frame.querySelector('[data-nova-video-play]');
    var source = frame.querySelector('[data-nova-video-source]');
    if (!button || !source) return;

    button.addEventListener('click', function () {
      var markup = source.content ? source.content.cloneNode(true) : null;
      if (!markup) return;
      frame.classList.add('is-playing');
      var media = frame.querySelector('.nova-video__media');
      if (media) {
        media.innerHTML = '';
        media.appendChild(markup);
        var video = media.querySelector('video');
        if (video) {
          video.setAttribute('autoplay', '');
          var playback = video.play();
          if (playback && typeof playback.catch === 'function') {
            // Autoplay can be refused; the controls are still there.
            playback.catch(function () {});
          }
        }
      }
      button.remove();
    });
  }

  /* --- PDP gallery: thumbnails swap the main image ----------------------- */
  function initGallery(gallery) {
    var stage = gallery.querySelector('.nova-pdp__stage-img');
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-nova-thumb]'));
    if (!stage || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full');
        if (full) {
          // Drop srcset so the browser cannot keep serving the old candidate.
          stage.removeAttribute('srcset');
          stage.src = full;
        }
        thumbs.forEach(function (other) {
          other.classList.toggle('is-active', other === thumb);
        });
      });
    });
  }

  /* --- Quantity stepper -------------------------------------------------- */
  function initQty(widget) {
    var input = widget.querySelector('input');
    if (!input) return;
    var down = widget.querySelector('[data-nova-qty-down]');
    var up = widget.querySelector('[data-nova-qty-up]');

    function nudge(delta) {
      var min = parseInt(input.getAttribute('min') || '1', 10);
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = min;
      input.value = String(Math.max(min, value + delta));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (down) down.addEventListener('click', function () { nudge(-1); });
    if (up) up.addEventListener('click', function () { nudge(1); });
  }

  /* --- Scroll-scrubbed hero video ---------------------------------------- */
  function initScrub(wrapper) {
    var video = wrapper.querySelector('[data-nova-scrub-video]');
    if (!video) return;

    // Reduced motion keeps the poster frame and never seeks.
    if (reduceMotion) return;

    var duration = 0;
    var target = 0;
    var current = 0;
    var ticking = false;

    function progress() {
      var rect = wrapper.getBoundingClientRect();
      // 0 when the wrapper's top hits the viewport bottom, 1 once its bottom
      // has passed the viewport top: a full pass of the element through view.
      var span = rect.height + window.innerHeight;
      var travelled = window.innerHeight - rect.top;
      return Math.min(1, Math.max(0, travelled / span));
    }

    function render() {
      // Ease toward the target so a flung scroll does not stutter the decoder.
      current += (target - current) * 0.12;
      if (Math.abs(target - current) < 0.001) current = target;
      if (video.readyState >= 1 && duration > 0) {
        try {
          video.currentTime = current * duration;
        } catch (e) {
          /* Seeking can throw while the media is still being prepared. */
        }
      }
      if (Math.abs(target - current) > 0.0005) {
        requestAnimationFrame(render);
      } else {
        ticking = false;
      }
    }

    function onScroll() {
      target = progress();
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(render);
      }
    }

    function start() {
      duration = video.duration;
      if (!duration || isNaN(duration)) return;
      wrapper.classList.add('is-scrubbing');
      target = progress();
      current = target;
      try {
        video.currentTime = current * duration;
      } catch (e) {
        /* ignore */
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }

    if (video.readyState >= 1) {
      start();
    } else {
      video.addEventListener('loadedmetadata', start, { once: true });
    }

    // Some browsers will not fetch a video that is never played; nudging it
    // with a muted play/pause primes the buffer so seeking is responsive.
    video.play().then(function () {
      video.pause();
    }).catch(function () {
      /* Autoplay refused: metadata still loads and seeking still works. */
    });
  }

  /* --- Contact modal ------------------------------------------------------ */
  function initContactModal(dialog) {
    if (dialog.dataset.novaContactReady) return;
    dialog.dataset.novaContactReady = 'true';

    function open() {
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        // No <dialog> support: fall back to the real /pages/contact form.
        window.location.href = '/pages/contact';
      }
    }

    function close() {
      if (dialog.open) dialog.close();
    }

    // Any link to #contact opens it, so buttons can be wired from the editor
    // without a code change.
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('a[href$="#contact"], [data-nova-contact-open]');
      if (opener) {
        event.preventDefault();
        open();
        return;
      }
      if (event.target.closest('[data-nova-contact-close]')) {
        event.preventDefault();
        close();
      }
    });

    // Clicking the backdrop closes; clicking the panel must not.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    // Shopify redirects back with ?contact_posted=true after a successful send.
    // Reopen so the confirmation is actually seen.
    if (dialog.querySelector('[data-nova-contact-posted]')) open();
  }

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-nova-showcase]').forEach(initShowcase);
    scope.querySelectorAll('[data-nova-accordion]').forEach(initAccordion);
    scope.querySelectorAll('.nova-quotes').forEach(initScroller);
    scope.querySelectorAll('[data-nova-video]').forEach(initVideo);
    scope.querySelectorAll('[data-nova-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-nova-qty]').forEach(initQty);
    scope.querySelectorAll('[data-nova-contact]').forEach(initContactModal);
    scope.querySelectorAll('[data-nova-scrub]').forEach(initScrub);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
